import { Component, Inject } from '@angular/core';
import { OrderApi, OrderDTO, PaymentDTO } from '@lkp/parking-sdk';
import { ORDER_API } from '../../core/providers/api.module';
import { DateService } from 'src/app/core/services/date.service';
import { PageEvent } from '@angular/material/paginator';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import {
  GetOrder,
  OrderSearchListener,
} from 'src/app/core/states/orders/orders.action';
import dayjs from 'dayjs';

@Component({
    selector: 'app-orders',
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.scss'],
    standalone: false
})
export class OrdersComponent {
  orders?: OrderDTO[];
  sortedOrders?: OrderDTO[];
  displayedColumns?: string[];
  searchAndFilterForm?: FormGroup;
  searchInput = new FormControl<string | undefined>({
    value: undefined,
    disabled: false,
  });
  readonly onDestroy$ = new Subject<void>();

  /* Paginator */
  pageSize: number = 50;
  pageIndex: number = 0;
  totalOrders: number = 0;

  showSpinner: boolean = false;

  constructor(
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    readonly dateService: DateService,
    readonly formBuilder: FormBuilder,
    readonly router: Router,
    readonly store: Store,
    actions$: Actions
  ) {
    actions$
      .pipe(
        ofActionDispatched(OrderSearchListener),
        map((action: OrderSearchListener) => action.term),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe((term) => {
        this.getOrders(undefined, term);
      });
  }

  ngOnInit() {
    this.displayedColumns = [
      'orderNumber',
      'bookings',
      'prepaid',
      'features',
      'paymentStatus',
      'paymentMethod',
      'createdDate',
      'departureDate',
      'handledInvoice',
      'totalAmount',
    ];

    const now = new Date();
    const oneYearBack = new Date(
      now.getFullYear() - 1,
      now.getMonth(),
      now.getDate()
    );

    /* Create form for date range */
    this.searchAndFilterForm = this.formBuilder.group({
      from: new FormControl<Date | undefined>(oneYearBack),
      to: new FormControl<Date | undefined>(now),
      paymentStatus: new FormControl<string>('all'),
      paymentMethod: new FormControl<string>('all'),
    });

    this.getOrders();

    /* Listen to input changes */
    this.searchAndFilterForm
      ?.get('from')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.getOrders();
      });

    this.searchAndFilterForm
      ?.get('to')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.getOrders();
      });

    this.searchAndFilterForm
      ?.get('paymentStatus')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.getOrders();
      });

    this.searchAndFilterForm
      ?.get('paymentMethod')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.getOrders();
      });

    this.searchInput.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.pageIndex = 0;
        this.showSpinner = true;
        this.store.dispatch(new OrderSearchListener(v?.trim() || undefined));
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  uniquePaymentMethods(payments: PaymentDTO[]) {
    return [...new Set(payments.map((item) => item.paymentMethod!.label!))];
  }

  showOrder(row: any) {
    /* Open single page order */
    if (row?.orderId != undefined)
      this.store
        .dispatch(new GetOrder(row.orderId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          row.orderId && this.router.navigate(['order/', row.orderId]);
        });
  }

  getOrders(sorting?: 'asc' | 'desc', term?: string) {
    this.showSpinner = true;
    const now = new Date();
    const oneMonthBack = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    let paymentMethod =
      this.searchAndFilterForm?.controls['paymentMethod']?.value;
    if (paymentMethod === 'all') paymentMethod = undefined;

    let paymentStatus =
      this.searchAndFilterForm?.controls['paymentStatus']?.value;
    if (paymentStatus === 'all') paymentStatus = undefined;

    /* Search term */
    let searchTerm = this.searchInput.value;
    searchTerm = searchTerm
      ? searchTerm.toString().replace(/\s/g, '')
      : undefined;

    /* Make sure empty strings become undefined */
    searchTerm = searchTerm === '' ? undefined : searchTerm;

    const fromDateControl =
      this.searchAndFilterForm?.controls['from'].value || oneMonthBack;
    const toDateControl =
      this.searchAndFilterForm?.controls['to'].value || now;

    const fromDate = dayjs(fromDateControl).startOf('day').toDate();
    const toDate = dayjs(toDateControl).startOf('day').add(1, 'day').toDate();

    if (
      this.searchAndFilterForm?.controls['from'].value &&
      this.searchAndFilterForm?.controls['to'].value
    ) {
      this.orderApi
        .getOrdersAdmin({
          page: this.pageIndex,
          size: this.pageSize,
          sort: 'createdDate,' + (sorting || 'desc'),
          orderId: undefined,
          orderItemId: undefined,
          bookingId: undefined,
          featureId: undefined,
          createdFromDate: fromDate,
          createdToDate: toDate,
          searchTerm: searchTerm,
          paymentMethodId: paymentMethod,
          paymentStatusId: paymentStatus,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.orders = response.content;
            this.totalOrders = response.totalElements || 0;
            this.showSpinner = false;
          },
          error: (error) => {
            console.error('Error fetching bookings:', error);
            this.showSpinner = false;
          },
        });
    }
  }

  getOrderItems(
    order: OrderDTO,
    type: 'RESOURCE' | 'FEATURE' | 'PREPAIDTICKET'
  ) {
    let result: any[] = [];
    if (order.orderItems?.length) {
      order.orderItems?.forEach((item) => {
        if (item.orderItemType === type) {
          result.push(item);
        }
      });
    }

    return result;
  }

  formatDate(date?: Date | string) {
    return date ? this.dateService.formatDate(date) : '-';
  }

  paginator(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.getOrders();
  }

  sortChange(e: Sort) {
    if (!this.orders) return;

    this.orders = [...this.orders]?.sort((a, b) => {
      const isAsc = e.direction === 'asc';

      switch (e.active) {
        case 'createdDate':
          return this.compare(
            (a.createdDate && new Date(a.createdDate).getTime()) || 0,
            (b.createdDate && new Date(b.createdDate).getTime()) || 0,
            isAsc
          );
        case 'departureDate':
          let departureDateA = this.getOrderDepartureDate(a);
          let departureDateB = this.getOrderDepartureDate(b);
          return this.compare(
            (departureDateA && new Date(departureDateA).getTime()) || 0,
            (departureDateB && new Date(departureDateB).getTime()) || 0,
            isAsc
          );
        case 'handledInvoice':
          return this.compare(
            this.invoiceStatus(a),
            this.invoiceStatus(b),
            isAsc
          );
        default:
          return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  getOrderDepartureDate(order: OrderDTO): Date | undefined {
    let booking = order.orderItems?.filter(
      (item) => item.orderItemType === 'RESOURCE'
    )[0]?.booking;
    return booking?.departureDate;
  }

  getCompanyName(order: OrderDTO): string | undefined {
    let invoice = order?.payments?.filter(
      (payment) => payment.paymentMethod?.paymentMethodId === 'INVOICE'
    )[0]?.paymentInvoice;
    return invoice?.name;
  }

  invoiceStatus(order: OrderDTO): string {
    let payments = order?.payments;
    if (
      payments?.length &&
      payments.some(
        (payment) => payment.paymentMethod?.paymentMethodId === 'INVOICE'
      )
    ) {
      let invoices = order?.payments?.filter(
        (payment) => payment.paymentMethod?.paymentMethodId === 'INVOICE'
      );
      return invoices?.every(
        (invoice) => invoice.paymentInvoice?.invoiceStatus === 'HANDLED'
      )
        ? 'Fakturerad'
        : 'Ej fakturerad';
    } else {
      return '-';
    }
  }
}
